/**
 * Legacy /catalogs|:documents/:tableName → /app/:objectName.
 * Keeps bookmarks and old sidebar pins working.
 */
import React, { useEffect, useState } from 'react'
import { Navigate, useParams, useSearchParams, Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Link from '@mui/material/Link'
import { useConnection } from '@/app/contexts/ConnectionContext'
import { resolveDesignObjectName } from '@/runtime/entityRoutes'

export const LegacyEntityRedirect: React.FC = () => {
  const { tableName } = useParams<{ tableName: string }>()
  const [searchParams] = useSearchParams()
  const { selectedConnection } = useConnection()
  const [objectName, setObjectName] = useState<string | null | undefined>(undefined)

  useEffect(() => {
    let cancelled = false
    if (!selectedConnection?.id || !tableName) {
      setObjectName(null)
      return
    }
    setObjectName(undefined)
    const hint = searchParams.get('object_name')?.trim() || null
    resolveDesignObjectName(selectedConnection.id, tableName, hint).then((name) => {
      if (!cancelled) setObjectName(name)
    })
    return () => {
      cancelled = true
    }
  }, [selectedConnection?.id, tableName, searchParams])

  if (objectName === undefined) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!objectName) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">
          Object not found for table &ldquo;{tableName}&rdquo;.{' '}
          <Link component={RouterLink} to="/dashboard" underline="hover">
            Go to dashboard
          </Link>
        </Alert>
      </Box>
    )
  }

  return <Navigate to={`/app/${encodeURIComponent(objectName)}`} replace />
}
